import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, Boxes, Wrench, ShieldCheck,
  BarChart3, ScrollText, LogOut, Search, Menu, X, Bell, Network, FileText,
  Settings as SettingsIcon,
} from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const ROLE_LABELS = {
  manufacturer: "Manufacturer",
  distributor: "Distributor",
  dealer: "Dealer",
};

function nav(role) {
  const base = [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", end: true },
  ];
  if (role === "manufacturer") {
    base.push(
      { to: "/app/distributors", label: "Distributors", icon: Building2, testId: "nav-distributors" },
      { to: "/app/dealers", label: "Dealers", icon: Users, testId: "nav-dealers" },
      { to: "/app/stock", label: "Stock", icon: Boxes, testId: "nav-stock" },
      { to: "/app/allocation", label: "Distribution", icon: Network, testId: "nav-allocation" },
      { to: "/app/installations", label: "Installations", icon: Wrench, testId: "nav-installations" },
      { to: "/app/warranties", label: "Warranties", icon: ShieldCheck, testId: "nav-warranties" },
      { to: "/app/reports", label: "Reports", icon: BarChart3, testId: "nav-reports" },
      { to: "/app/terms", label: "Terms", icon: FileText, testId: "nav-terms" },
      { to: "/app/audit", label: "Audit Log", icon: ScrollText, testId: "nav-audit" },
    );
  }
  if (role === "distributor") {
    base.push(
      { to: "/app/dealers", label: "Dealers", icon: Users, testId: "nav-dealers" },
      { to: "/app/stock", label: "Stock", icon: Boxes, testId: "nav-stock" },
      { to: "/app/installations", label: "Installations", icon: Wrench, testId: "nav-installations" },
      { to: "/app/warranties", label: "Warranties", icon: ShieldCheck, testId: "nav-warranties" },
      { to: "/app/reports", label: "Reports", icon: BarChart3, testId: "nav-reports" },
    );
  }
  if (role === "dealer") {
    base.push(
      { to: "/app/stock", label: "Stock", icon: Boxes, testId: "nav-stock" },
      { to: "/app/installations", label: "Installations", icon: Wrench, testId: "nav-installations" },
      { to: "/app/warranties", label: "Warranties", icon: ShieldCheck, testId: "nav-warranties" },
    );
  }
  base.push({ to: "/app/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" });
  return base;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav_items = nav(user?.role || "dealer");
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const doSearch = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults([]); return; }
    try {
      const r = await api.get(`/search?q=${encodeURIComponent(val)}`);
      setResults(r.data.results || []);
    } catch { setResults([]); }
  };

  const onClickResult = (r) => {
    setSearchOpen(false);
    setQ("");
    setResults([]);
    if (r.type === "warranty") navigate("/app/warranties?q=" + encodeURIComponent(r.label.split(" • ")[0]));
    else if (r.type === "stock") navigate("/app/stock?q=" + encodeURIComponent(r.label.split(" • ")[0]));
    else if (r.type === "distributor") navigate("/app/distributors");
    else if (r.type === "dealer") navigate("/app/dealers");
  };

  return (
    <div className="min-h-screen flex bg-[#FFFFFF]">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-[#FFFFFF] border-r border-[#E5E7EB] transform transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-5 border-b border-[#E5E7EB]">
          <Link to="/app" className="block">
            <Logo size="md" />
          </Link>
        </div>
        <nav className="px-3 py-4 space-y-1">
          {nav_items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testId}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-[#E63946]/10 text-[#E63946] border border-[#E63946]/30"
                      : "text-[#475569] hover:text-slate-900 hover:bg-[#F8FAFC] border border-transparent"
                  }`
                }
              >
                <Icon size={16} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-[#E5E7EB]">
          <div className="px-3 py-2 rounded-md bg-[#FFFFFF] border border-[#E5E7EB]">
            <div className="text-xs text-[#475569]">Signed in as</div>
            <div className="text-sm text-slate-900 truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] mt-1">
              {ROLE_LABELS[user?.role] || user?.role}
            </div>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/"); }}
              className="mt-3 w-full flex items-center justify-center gap-2 text-xs text-[#475569] hover:text-slate-900 py-1.5 border border-[#E5E7EB] rounded-md hover:bg-[#F8FAFC]"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-[#E5E7EB]">
          <div className="flex items-center gap-3 px-4 lg:px-8 py-3">
            <button className="lg:hidden text-slate-900" onClick={() => setOpen(!open)} data-testid="sidebar-toggle">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="flex-1 relative">
              <div className="relative max-w-xl">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input
                  data-testid="global-search-input"
                  value={q}
                  onChange={(e) => doSearch(e.target.value)}
                  onFocus={() => setSearchOpen(true)}
                  onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                  placeholder="Search distributors, dealers, stock, warranties, vehicles..."
                  className="w-full pl-9 pr-16 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 placeholder:text-[#64748B] focus:outline-none focus:border-[#E63946]"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 kbd">⌘ K</span>
                {searchOpen && results.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-2 max-h-96 overflow-auto bg-[#FFFFFF] border border-[#E5E7EB] rounded-md shadow-2xl">
                    {results.map((r, i) => (
                      <button
                        key={i}
                        data-testid={`search-result-${r.type}`}
                        onMouseDown={() => onClickResult(r)}
                        className="w-full text-left px-3 py-2 hover:bg-[#F8FAFC] text-sm border-b border-[#E5E7EB] last:border-b-0"
                      >
                        <span className="text-[10px] uppercase text-[#E63946] mr-2">{r.type}</span>
                        <span className="text-slate-900">{r.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="hidden md:block text-xs text-[#475569]">
              {ROLE_LABELS[user?.role]} Console
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
